import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПАДЕНИЕ ОТРИСОВКИ НЕ ДОЛЖНО ГАСИТЬ ВЕСЬ КАБИНЕТ.
 *
 * 30.08.2026 владелец: «нажал запустить тест и сразу серый экран».
 * Ошибка в ОДНОМ окне уносила страницу целиком: React снимает всё
 * дерево, если некому поймать. Человек видел пустой прямоугольник
 * и не знал ни что случилось, ни что делать, ни — главное — сохранилась
 * ли его работа.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЧТО ЗДЕСЬ ВАЖНО СКАЗАТЬ ЧЕЛОВЕКУ, И ПОЧЕМУ ИМЕННО ЭТО.
 *
 * 1. РАБОТА НЕ ПОТЕРЯНА. Кабинет — тонкая оболочка: тест заводится
 *    на сервере, и упавшая отрисовка к нему не относится. Первый страх
 *    при пустом экране — «я что-то сломал»; он неверен, и молчать
 *    об этом жестоко.
 *
 * 2. ТЕКСТ ОШИБКИ. Не «что-то пошло не так»: эта строка — единственное,
 *    что отличает починку за час от переписки на день. Она же
 *    отправляется нам вместе с обращением.
 *
 * 3. ЧТО ДЕЛАТЬ. Обновить страницу. Кнопка рядом, а не в совете.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ГРАНИЦ ДВЕ, И ЭТО НАМЕРЕННО.
 *
 * Внешняя — над всем кабинетом: ловит всё, что не поймано ближе.
 * Внутренняя — вокруг содержимого окна: окно ломается чаще прочего
 * (в нём чужие данные во всех сочетаниях), и его падение обязано
 * оставлять страницу живой. Одна внешняя граница спасла бы от серого
 * экрана, но человек всё равно терял бы экран целиком.
 * ═══════════════════════════════════════════════════════════════════════
 */
interface Состояние {
  беда: Error | null;
}

export class ГраницаОшибки extends Component<
  {
    children: ReactNode;
    /** Что именно упало — словами человека, а не именем файла. */
    место: string;
    /** Внутренняя граница рисуется скромнее: она внутри окна. */
    внутри?: boolean;
  },
  Состояние
> {
  override state: Состояние = { беда: null };

  static getDerivedStateFromError(беда: Error): Состояние {
    return { беда };
  }

  override componentDidCatch(беда: Error, где: ErrorInfo): void {
    /* В КОНСОЛЬ — ПОЛНОСТЬЮ. Человек пришлёт снимок консоли, и там
       должно быть дерево вызовов, а не только строка, которую мы
       нарисовали. */
    console.error(`[БИДРА] ${this.props.место}:`, беда, где.componentStack);
  }

  override render(): ReactNode {
    const { беда } = this.state;
    if (беда === null) return this.props.children;

    return (
      <div
        role="alert"
        className="rk-text-sm"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-2)',
          padding: this.props.внутри === true ? 'var(--sp-4)' : 'var(--sp-6)',
          margin: this.props.внутри === true ? 0 : 'var(--sp-6) auto',
          maxWidth: 640,
          borderRadius: 'var(--r-md)',
          background: 'var(--bad-wash)',
          color: 'var(--bad-ink)',
          textWrap: 'pretty',
        }}
      >
        <b className="rk-text-h2">Не удалось показать: {this.props.место}</b>
        <span>
          Это ошибка показа, а не ваших данных: всё, что вы успели сделать, сохранено
          на сервере. Обновите страницу — обычно этого хватает.
        </span>
        <code
          style={{
            padding: 'var(--sp-2)',
            borderRadius: 'var(--r-sm)',
            background: 'var(--bg-surface)',
            color: 'var(--ink-2)',
            overflowWrap: 'anywhere',
          }}
        >
          {беда.message || String(беда)}
        </code>
        <div className="flex flex-wrap" style={{ gap: 'var(--sp-2)' }}>
          <button className="rk-button" onClick={() => window.location.reload()}>
            Обновить страницу
          </button>
          {/* ПОПРОБОВАТЬ ЕЩЁ РАЗ — БЕЗ ПЕРЕЗАГРУЗКИ. Часть падений
              случайна (данные приехали в неудачный момент), и терять
              из-за них весь экран незачем. */}
          <button className="rk-button-quiet" onClick={() => this.setState({ беда: null })}>
            Попробовать ещё раз
          </button>
        </div>
      </div>
    );
  }
}
